import { FC, useCallback, useEffect, useMemo, useState } from "react"
import ColorGroup from "./ColorGroup"
import AlertSetting from "../AlertSetting"
import Actions from "../AlertSetting/Actions"
import { IconColor } from "@/components/shared/Icon"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { AlertVariant } from "@/types/alert"
import "./index.scss"

export interface AlertColorForm {
   colorCard: string
   colorTypeface: string
}

interface AlertColorSettingsProps {
   colorCard: string
   colorTypeface: string
   onSave: (form: AlertColorForm) => void
}

const AlertColorSettings: FC<AlertColorSettingsProps> = ({
   colorCard,
   colorTypeface,
   onSave,
}) => {
   const initialColorForm = useMemo((): AlertColorForm => {
      return {
         colorCard,
         colorTypeface,
      }
   }, [colorCard, colorTypeface])

   const [isEditing, setIsEditing] = useState(false)
   const [form, setState] =
      useFormStateHandler<AlertColorForm>(initialColorForm)

   useEffect(() => {
      setState({ colorCard, colorTypeface })
   }, [colorCard, colorTypeface, setState])

   const handleCancel = useCallback(() => {
      setState({ colorCard, colorTypeface })
      setIsEditing(false)
   }, [colorCard, setState, colorTypeface])

   const handleSave = useCallback(() => {
      onSave(form)
      setIsEditing(false)
   }, [onSave, form])

   const isCardColorWhite = useMemo(() => {
      return Object.keys(AlertVariant).some(
         (key) => key === form.colorCard && key === "#ffffff"
      )
   }, [form.colorCard])

   const isTypefaceColorWhite = useMemo(() => {
      return Object.keys(AlertVariant).some(
         (key) => key === form.colorTypeface && key === "#ffffff"
      )
   }, [form.colorTypeface])

   const isFormChanged = useMemo(() => {
      return (
         initialColorForm.colorCard !== form.colorCard ||
         initialColorForm.colorTypeface !== form.colorTypeface
      )
   }, [initialColorForm, form])

   return (
      <AlertSetting
         iconColor={IconColor.HONOLULU_BLUE}
         iconName="visual"
         title="Visual"
      >
         <div className="alert-color-setting">
            <div className="alert-color-setting__title">
               <span>Color selection</span>
               <Actions
                  isDisabled={!isFormChanged}
                  isEditing={isEditing}
                  onCancel={handleCancel}
                  onEdit={() => setIsEditing(true)}
                  onSave={handleSave}
               />
            </div>
            <ColorGroup
               label="Alarm card color"
               valueKey="colorCard"
               isEditing={isEditing}
               form={form}
               setFormState={setState}
               iconColor={
                  isCardColorWhite ? IconColor.GUNMETAL : IconColor.WHITE
               }
            />
            <ColorGroup
               label="Text and icon color"
               valueKey="colorTypeface"
               isEditing={isEditing}
               form={form}
               setFormState={setState}
               iconColor={
                  isTypefaceColorWhite ? IconColor.GUNMETAL : IconColor.WHITE
               }
            />
         </div>
      </AlertSetting>
   )
}

export default AlertColorSettings
